import React from "react"
import {Link} from "react-router-dom"
import moment from "moment"
import {plural} from "utils/text_utils"


const SEVERITY_ORDER = {error: 0, warning: 1, success: 2}


export function isReportSupervisor(admin, username, shifts, hasTag) {
    if (admin) return true
    if (!username) return false
    for (const a in shifts ?? {}) {
        if (shifts[a].head?.username === username) return true
    }
    return hasTag(username, 'koordinator-smena') || hasTag(username, 'nadzornik-izveštaja')
}


export function getDateLabel(date) {
    const isToday = moment(date).isSame(moment(), 'day')
    const isYesterday = moment(date).isSame(moment().subtract(1, 'day'), 'day')
    const isDayBefore = moment(date).isSame(moment().subtract(2, 'days'), 'day')
    const formatted = moment(date).format("D. MMMM")
    if (isToday) return "danas, " + formatted
    if (isYesterday) return "juče, " + formatted
    if (isDayBefore) return "prekjuče, " + formatted
    return formatted
}


function getReportSeverity(date, report) {
    if (report) return null

    const isToday = moment(date).isSame(moment(), 'day')
    if (isToday) {
        const now = new Date()
        const hour = now.getHours() + now.getMinutes() / 60
        if (hour < 21.5) return null
        return "warning"
    }
    return "error"
}


export function collectReportWarnings(area, dates, queryData, reportDate) {
    const items = []

    for (let i = 0; i < dates.length; i++) {
        const date = dates[i]
        const data = queryData[i]
        if (!data) continue

        const isToday = moment(date).isSame(moment(), 'day')
        const dateLabel = getDateLabel(date)
        const reportLink = encodeURI(`/report/${area}/${date}`)

        // Non-submitted rescues
        const openRescues = data.rescues?.filter(r => !r.submitted) ?? []
        if (openRescues.length > 0) {
            const rescueSeverity = isToday && new Date().getHours() < 21 ? "warning" : "error"
            const n = openRescues.length
            const label = `${n} ${plural(n, "nezaključena povredna lista", "nezaključene povredne liste", "nezaključenih povrednih listi")}`
            items.push({
                key: `rescue-${date}`,
                severity: rescueSeverity,
                date,
                content: <span>{label} (<Link to={reportLink}>{dateLabel}</Link>)</span>
            })
        }

        // Unassigned rescues (only if daily report not yet submitted)
        if (!data.report) {
            for (const rescue of (data.unassigned ?? [])) {
                const subjectName = [rescue.subject?.given_name, rescue.subject?.family_name].filter(Boolean).join(' ')
                items.push({
                    key: `unassigned-${rescue.id}`,
                    severity: "warning",
                    date,
                    content: <span>OPU lista na skijalištu {area} <Link to={`/rescue/${rescue.id}`}>{subjectName || `#${rescue.short_id}`}</Link> ({dateLabel})</span>
                })
            }
        }

        // Report submission status
        const reportSev = getReportSeverity(date, data.report)
        if (reportSev) {
            items.push({
                key: `report-${date}`,
                severity: reportSev,
                date,
                content: <span><Link to={reportLink}>Dnevni izveštaj za {dateLabel}</Link> nije zaključen</span>
            })
        }

        // Report not sent
        if (data.report && data.report.sent === false) {
            items.push({
                key: `sent-${date}`,
                severity: "warning",
                date,
                content: <span><Link to={reportLink}>Dnevni izveštaj za {dateLabel}</Link> nije poslat</span>
            })
        }

        // Rescue count mismatch between report and current list
        if (data.report?.rescues && data.report.rescues.length !== (data.rescues?.length ?? 0)) {
            items.push({
                key: `mismatch-${date}`,
                severity: "error",
                date,
                content: <span><Link to={reportLink}>Dnevni izveštaj za {dateLabel}</Link>: broj povrednih listi u izveštaju ({data.report.rescues.length}) se ne slaže sa spiskom ({data.rescues.length})</span>
            })
        }

        // Report re-submitted after send
        if (data.report?.sent && data.report?.submitted && data.report.sent < data.report.submitted) {
            items.push({
                key: `resub-${date}`,
                severity: "warning",
                date,
                content: <span><Link to={reportLink}>Dnevni izveštaj za {dateLabel}</Link> je ponovo zaključen posle slanja</span>
            })
        }

        // Done
        if (date === reportDate && data.report && data.report.sent && !(data.report.sent < data.report.submitted)) {
            items.push({
                key: `done-${date}`,
                severity: "success",
                date,
                content: <span><Link to={reportLink}>Dnevni izveštaj za {dateLabel}</Link> je zaključen i poslat</span>
            })
        }
    }

    items.sort((a, b) => {
        const s = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
        if (s !== 0) return s
        return a.date < b.date ? -1 : a.date > b.date ? 1 : 0
    })

    return items
}


export function severityIcon(severity) {
    if (severity === "error") return "fa-circle-xmark"
    if (severity === "success") return "fa-check"
    return "fa-triangle-exclamation"
}
